import { API_BASE_URL } from './client';

// Group Management API
export const getGroups = async (keycloak, includeInactive = false) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');
  
  const response = await fetch(`${API_BASE_URL}/groups/?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch groups');
  }
  return await response.json();
};

export const getGroup = async (keycloak, groupId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch group');
  }
  return await response.json();
};

export const createGroup = async (keycloak, groupData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(groupData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create group');
  }
  return await response.json();
};

export const updateGroup = async (keycloak, groupId, groupData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(groupData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update group');
  }
  return await response.json();
};

export const deleteGroup = async (keycloak, groupId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete group');
  }
  return await response.json();
};

export const getGroupMembers = async (keycloak, groupId, includeInactive = false) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');
  
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch group members');
  }
  return await response.json();
};

export const addMemberToGroup = async (keycloak, groupId, userId, role = 'member') => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId, role }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add member to group');
  }
  return await response.json();
};

export const updateGroupMember = async (keycloak, groupId, userId, memberData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${userId}/`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(memberData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update member');
  }
  return await response.json();
};

export const removeMemberFromGroup = async (keycloak, groupId, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${userId}/`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to remove member from group');
  }
  return await response.json();
};

export const getUserGroups = async (keycloak, userId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/user/${userId}/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch user groups');
  }
  return await response.json();
};

export const getGroupUsage = async (keycloak, groupId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/${groupId}/usage/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch group usage');
  }
  return await response.json();
};

export const syncGroupsFromKeycloak = async (keycloak) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/groups/sync-keycloak/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to sync groups from Keycloak');
  }
  return await response.json();
};
